<script setup>
import { computed } from 'vue';

const props = defineProps({
    level: { type: String, default: '' },
    size: { type: String, default: 'md' }
});

/** 依等級代碼／名稱推斷視覺層級（僅 UI，不影響業務） */
const tier = computed(() => {
    const raw = String(props.level || '').toLowerCase();
    if (!raw) return 'default';
    if (/[saＳＡ]|高|senior|expert|5|四/.test(raw)) return 'elite';
    if (/[bcＢＣ]|中|mid|inter|3|4|三|二/.test(raw)) return 'pro';
    if (/[dＤ]|初|junior|1|2|一/.test(raw)) return 'base';
    return 'default';
});

const sizeClass = computed(() =>
    props.size === 'sm' ? 'cw-level-badge--sm' : 'cw-level-badge--md'
);
</script>

<template>
    <span
        v-if="level"
        class="cw-level-badge"
        :class="[`cw-level-badge--${tier}`, sizeClass]"
    >
        <span class="cw-level-badge__shine" aria-hidden="true" />
        <span class="cw-level-badge__label">{{ level }}</span>
    </span>
</template>

<style scoped>
.cw-level-badge {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 999px;
    font-weight: 600;
    letter-spacing: 0.06em;
    line-height: 1;
    white-space: nowrap;
    overflow: hidden;
    vertical-align: middle;
    box-shadow:
        0 1px 2px rgba(39, 43, 54, 0.06),
        inset 0 1px 0 rgba(255, 255, 255, 0.45);
}

.cw-level-badge--md {
    padding: 5px 14px 5px 12px;
    font-size: 12px;
}

.cw-level-badge--sm {
    padding: 3px 10px 3px 9px;
    font-size: 11px;
}

.cw-level-badge__shine {
    position: absolute;
    inset: 0;
    background: linear-gradient(
        105deg,
        transparent 0%,
        rgba(255, 255, 255, 0.35) 45%,
        transparent 70%
    );
    pointer-events: none;
}

.cw-level-badge__label {
    position: relative;
    z-index: 1;
}

.cw-level-badge--default {
    color: #1e4d3f;
    border: 1px solid rgba(139, 105, 20, 0.45);
    background: linear-gradient(135deg, #f0faf6 0%, #dff5eb 55%, #c8ebdc 100%);
}

.cw-level-badge--base {
    color: #3d4f6f;
    border: 1px solid rgba(120, 144, 180, 0.4);
    background: linear-gradient(135deg, #f4f7fc 0%, #e4ebf8 55%, #d5e0f2 100%);
}

.cw-level-badge--pro {
    color: #5c4a12;
    border: 1px solid rgba(201, 162, 39, 0.45);
    background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 48%, #fde68a 100%);
}

.cw-level-badge--elite {
    color: #4a2c0f;
    border: 1px solid rgba(180, 120, 40, 0.5);
    background: linear-gradient(
        135deg,
        #fff9ed 0%,
        #fde6b8 35%,
        #f5c76a 70%,
        #e8a838 100%
    );
    box-shadow:
        0 2px 8px rgba(232, 168, 56, 0.22),
        inset 0 1px 0 rgba(255, 255, 255, 0.5);
}
</style>
